import React, { useEffect, useState, useContext } from "react";
import { Button, Table, Modal, Form, Spinner, Alert } from "react-bootstrap";
import { AuthContext } from "../../hooks/auth/AuthContext";
import { getTenants, createTenant, updateTenant, deleteTenant } from "../../services/dataService";
import Head from "../../components/layout/head/Head";
import "../../styles/tenants-page-styles.css";
import { toast } from "react-toastify";

const TenantsPage = () => {
    const authContext = useContext(AuthContext);
    const { user } = authContext;

    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentTenant, setCurrentTenant] = useState(null);
    const [incluirInactivos, setIncluirInactivos] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        id: "",
        ruc: "",
        dv: 0,
        iTipCont: 1,
        cTipReg: null,
        dNomEmi: "",
        dNomFanEmi: "",
        activo: true,
    });

    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadTenants();
        // eslint-disable-next-line
    }, [incluirInactivos]);

    const loadTenants = async () => {
        try {
            setLoading(true);
            const data = await getTenants(user.token, incluirInactivos);
            setTenants(data);
        } catch (error) {
            console.error("Error al cargar tenants:", error);
            toast.error("Error al cargar los tenants");
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = (tenant = null) => {
        if (tenant) {
            setEditMode(true);
            setCurrentTenant(tenant);
            setFormData({
                id: tenant.id,
                ruc: tenant.ruc || "",
                dv: tenant.dv || 0,
                iTipCont: tenant.iTipCont || 1,
                cTipReg: tenant.cTipReg || null,
                dNomEmi: tenant.dNomEmi || "",
                dNomFanEmi: tenant.dNomFanEmi || "",
                activo: tenant.activo,
            });
        } else {
            setEditMode(false);
            setCurrentTenant(null);
            setFormData({
                id: "",
                ruc: "",
                dv: 0,
                iTipCont: 1,
                cTipReg: null,
                dNomEmi: "",
                dNomFanEmi: "",
                activo: true,
            });
        }
        setFormErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditMode(false);
        setCurrentTenant(null);
        setFormErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        
        // Limpiar error del campo
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.ruc || formData.ruc.trim() === "") {
            errors.ruc = "El RUC es obligatorio";
        } else if (!/^\d+$/.test(formData.ruc)) {
            errors.ruc = "El RUC debe contener solo números";
        }

        if (!formData.dNomEmi || formData.dNomEmi.trim() === "") {
            errors.dNomEmi = "El nombre del emisor es obligatorio";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const tenantData = {
                ruc: formData.ruc,
                dv: parseInt(formData.dv) || 0,
                iTipCont: parseInt(formData.iTipCont) || 1,
                cTipReg: formData.cTipReg ? parseInt(formData.cTipReg) : null,
                dNomEmi: formData.dNomEmi,
                dNomFanEmi: formData.dNomFanEmi || "",
                activo: formData.activo,
            };

            // Solo incluir id si estamos en modo edición
            if (editMode && formData.id) {
                tenantData.id = formData.id;
            }

            if (editMode) {
                await updateTenant(currentTenant.id, tenantData, user.token);
                toast.success("Tenant actualizado correctamente");
            } else {
                await createTenant(tenantData, user.token);
                toast.success("Tenant creado correctamente");
            }

            handleCloseModal();
            loadTenants();
        } catch (error) {
            console.error("Error al guardar tenant:", error);
            
            // Manejar errores de validación del backend
            if (error.response?.data) {
                const errorData = error.response.data;
                
                // Si hay errores de validación específicos
                if (errorData.errors) {
                    const backendErrors = {};
                    
                    // Mapear errores del backend a campos del formulario
                    Object.keys(errorData.errors).forEach(key => {
                        const fieldName = key.toLowerCase().replace('$.', '').replace('modelo.', '');
                        const messages = errorData.errors[key];
                        
                        // Mapear nombres de campos del backend a nombres del frontend
                        const fieldMapping = {
                            'ruc': 'ruc',
                            'dnomemi': 'dNomEmi',
                            'dv': 'dv',
                            'itipcont': 'iTipCont',
                            'ctipreg': 'cTipReg',
                            'dnomfanemi': 'dNomFanEmi'
                        };
                        
                        const frontendField = fieldMapping[fieldName] || fieldName;
                        backendErrors[frontendField] = Array.isArray(messages) ? messages[0] : messages;
                    });
                    
                    setFormErrors(backendErrors);
                    toast.error("Por favor corrija los errores en el formulario");
                }
                // Si es un error de RUC duplicado u otro error de negocio
                else if (typeof errorData === 'string') {
                    toast.error(errorData);
                }
                else if (errorData.message) {
                    toast.error(errorData.message);
                }
                else {
                    toast.error("Error al guardar el tenant");
                }
            } else {
                toast.error("Error al guardar el tenant");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas desactivar este tenant?")) {
            return;
        }

        try {
            await deleteTenant(id, user.token);
            toast.success("Tenant desactivado correctamente");
            loadTenants();
        } catch (error) {
            console.error("Error al eliminar tenant:", error);
            toast.error("Error al desactivar el tenant");
        }
    };

    const getTipoContribuyente = (iTipCont) => {
        const tipos = {
            1: "Persona Física",
            2: "Persona Jurídica",
        };
        return tipos[iTipCont] || "N/A";
    };

    if (loading) {
        return (
            <div className="tenants-loading">
                <Spinner animation="border" variant="primary" />
                <p>Cargando tenants...</p>
            </div>
        );
    }

    return (
        <>
            <Head title="Configuración - Tenants" />
            <div className="tenants-container">
                <div className="tenants-header">
                    <h2>Gestión de Tenants</h2>
                    <div className="tenants-actions">
                        <Form.Check
                            type="checkbox"
                            label="Incluir inactivos"
                            checked={incluirInactivos}
                            onChange={(e) => setIncluirInactivos(e.target.checked)}
                            className="me-3"
                        />
                        <Button variant="primary" onClick={() => handleShowModal()}>
                            Nuevo Tenant
                        </Button>
                    </div>
                </div>

                {tenants.length === 0 ? (
                    <Alert variant="info">No hay tenants registrados.</Alert>
                ) : (
                    <div className="tenants-table-container">
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>RUC</th>
                                    <th>DV</th>
                                    <th>Nombre Emisor</th>
                                    <th>Nombre Fantasía</th>
                                    <th>Tipo Contribuyente</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.map((tenant) => (
                                    <tr key={tenant.id} className={!tenant.activo ? "tenant-inactive" : ""}>
                                        <td>{tenant.ruc}</td>
                                        <td>{tenant.dv}</td>
                                        <td>{tenant.dNomEmi}</td>
                                        <td>{tenant.dNomFanEmi || "-"}</td>
                                        <td>{getTipoContribuyente(tenant.iTipCont)}</td>
                                        <td>
                                            <span className={`badge ${tenant.activo ? "bg-success" : "bg-secondary"}`}>
                                                {tenant.activo ? "Activo" : "Inactivo"}
                                            </span>
                                        </td>
                                        <td className="tenants-actions-cell">
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => handleShowModal(tenant)}
                                                className="me-2"
                                            >
                                                Editar
                                            </Button>
                                            {tenant.activo && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(tenant.id)}
                                                >
                                                    Desactivar
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Modal para Crear/Editar */}
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{editMode ? "Editar Tenant" : "Nuevo Tenant"}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>RUC *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="ruc"
                                    value={formData.ruc}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.ruc}
                                    placeholder="Ingrese el RUC"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.ruc}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Dígito Verificador (DV)</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="dv"
                                    value={formData.dv}
                                    onChange={handleInputChange}
                                    min="0"
                                    max="9"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Nombre del Emisor *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dNomEmi"
                                    value={formData.dNomEmi}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.dNomEmi}
                                    placeholder="Nombre completo del emisor"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.dNomEmi}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Nombre Fantasía</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dNomFanEmi"
                                    value={formData.dNomFanEmi}
                                    onChange={handleInputChange}
                                    placeholder="Nombre comercial (opcional)"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Tipo de Contribuyente *</Form.Label>
                                <Form.Select
                                    name="iTipCont"
                                    value={formData.iTipCont}
                                    onChange={handleInputChange}
                                >
                                    <option value={1}>Persona Física</option>
                                    <option value={2}>Persona Jurídica</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Código Tipo de Régimen</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="cTipReg"
                                    value={formData.cTipReg || ""}
                                    onChange={handleInputChange}
                                    placeholder="Opcional"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    name="activo"
                                    label="Activo"
                                    checked={formData.activo}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        Guardando...
                                    </>
                                ) : editMode ? (
                                    "Actualizar"
                                ) : (
                                    "Crear"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default TenantsPage;
